// src/hooks/useTokenBalance.ts
import { useState, useEffect } from 'react';
import { Connection, PublicKey } from '@solana/web3.js';
import { useDynamicContext } from "@dynamic-labs/sdk-react-core";
import type { ChapterLockStatus, UseTokenBalanceReturn } from '../types/book';
import {
  TOKEN_ADDRESS,
  ALCHEMY_RPC,
  BASE_REQUIREMENTS,
  MAX_MULTIPLIER,
  HOLDERS_PER_MULTIPLIER
} from '../constants';

export function useTokenBalance(): UseTokenBalanceReturn {
  const { user } = useDynamicContext();
  const [tokenBalance, setTokenBalance] = useState<number>(0);
  const [holderCount, setHolderCount] = useState<number>(0);
  const [chapterLockStatus, setChapterLockStatus] = useState<ChapterLockStatus>({
    1: false,
    2: true,
    3: true,
    4: true,
    5: true,
    6: true,
    7: true,
    8: true,
    9: true,
    10: true,
  });

  // Calculate multiplier based on holder count
  const getMultiplier = (holders: number) => {
    return Math.min(1 + (holders / HOLDERS_PER_MULTIPLIER), MAX_MULTIPLIER);
  };

  // Calculate dynamic requirements based on holder count
  const calculateRequirement = (baseRequirement: number, holders: number) => {
    const multiplier = getMultiplier(holders);
    return Math.floor(baseRequirement * multiplier);
  };

  // Get current requirements for all chapters
  const getCurrentRequirements = (holders: number) => {
    return Object.entries(BASE_REQUIREMENTS).reduce((acc, [chapter, base]) => ({
      ...acc,
      [chapter]: calculateRequirement(base, holders)
    }), {});
  };

  useEffect(() => {
    const fetchTokenData = async (walletAddress: string) => {
      try {
        const connection = new Connection(ALCHEMY_RPC, {
          commitment: 'confirmed',
          confirmTransactionInitialTimeout: 60000
        });

        const pubKey = new PublicKey(walletAddress);
        const response = await connection.getTokenAccountsByOwner(pubKey, {
          mint: new PublicKey(TOKEN_ADDRESS),
        });

        let totalBalance = 0;
        for (const account of response.value) {
          const accountInfo = await connection.getTokenAccountBalance(account.pubkey);
          if (accountInfo.value) {
            const amount = accountInfo.value.amount;
            const decimals = accountInfo.value.decimals;
            totalBalance += Math.floor(Number(amount) / Math.pow(10, decimals));
          }
        }

        console.log('Total balance:', totalBalance);
        setTokenBalance(totalBalance);

        // For holder count, we'll use a fixed number or fetch from an API
        const estimatedHolders = 250; // Replace with actual API call
        setHolderCount(estimatedHolders);

        // Update chapter lock status based on requirements
        const requirements = getCurrentRequirements(estimatedHolders);
        setChapterLockStatus(
          Object.entries(requirements).reduce((acc, [chapter, requirement]) => ({
            ...acc,
            [chapter]: totalBalance < requirement,
          }), {})
        );

      } catch (error) {
        console.error('Error fetching token data:', error);
        console.log('Wallet address:', walletAddress);
        console.log('Token address:', TOKEN_ADDRESS);
        // Keep chapter 1 unlocked even on error
        setChapterLockStatus(prev => ({...prev, 1: false}));
        setHolderCount(0);
        setTokenBalance(0);
      }
    };

    if (user?.verifiedCredentials?.[0]?.address) {
      console.log('Fetching data for wallet:', user.verifiedCredentials[0].address);
      fetchTokenData(user.verifiedCredentials[0].address);
    }
  }, [user]);

  return {
    tokenBalance,
    chapterLockStatus,
    holderCount,
    currentRequirements: getCurrentRequirements(holderCount)
  };
}
